import { InputGroupDisable } from "@funded-here-interface/common";
import { FC, useMemo } from "react";
import { Data, Spv, UserData } from "../../../types/org.type";
import { useStyles } from "./Organisation.styles";
import { capitalizeFirstLetter } from "@funded-here-interface/common/src/Utils/string";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import { PERSONA_DISPLAY_MAP } from "@funded-here-interface/common/src/constant/constant";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { Divider } from "@mantine/core";
import { codeToCountryNameMap } from "@funded-here-interface/common/src/constant/country";

interface OrganisationProps {
  data: Data | undefined;
  userData: UserData | undefined;
  role: Persona;
  spvData?: Spv;
}

interface FieldConfig {
  label: string;
  id: string;
  value: string | number;
  shouldShow?: boolean;
}

const Organisation: FC<OrganisationProps> = ({
  data,
  userData,
  role,
  spvData,
}) => {
  const { classes } = useStyles();

  // Extract and compute all field values
  const rawRoleName = userData?.orgRole?.[0]?.role?.name;
  const isDistributor = rawRoleName === Persona.DISTRIBUTOR;
  const isInvestor = role === Persona.INVESTOR;
  const shouldHideInvestmentAmount = [
    Persona.DISTRIBUTOR,
    Persona.MERCHANT_SPV,
    Persona.ISPV,
    Persona.MERCHANT,
  ].includes(rawRoleName as Persona);

  // Build onboarded as display value
  let onboardedAs = rawRoleName
    ? PERSONA_DISPLAY_MAP[rawRoleName] || capitalizeFirstLetter(rawRoleName)
    : "-";
  if (userData?.investorCategory?.category) {
    onboardedAs = `${capitalizeFirstLetter(
      userData?.investorCategory?.category
    )} ${onboardedAs}`;
  }

  // Define field configurations using useMemo for performance
  const leftColumnFields = useMemo<FieldConfig[]>(
    () => [
      {
        label: "Onboarded as",
        id: "onboarded_as",
        value: onboardedAs,
      },
      {
        label: "Date onboarded",
        id: "date_onboarded",
        value: data?.createdDate ? convertDate(data.createdDate) : "-",
      },
      {
        label: "Company Name",
        id: "company_name",
        value: data?.companyName || "-",
      },
      {
        label: "Registration Number",
        id: "registration_number",
        value: data?.companyRegistrationNo || "-",
      },
      {
        label: "Legal Structure",
        id: "legal_structure",
        value: data?.legalStructure || "-",
      },
      {
        label: "Country of Incorporation",
        id: "country_of_incorporation",
        value: isDistributor
          ? codeToCountryNameMap.get(data?.countryISOCode || "") || "-"
          : data?.countryOfIncorporation || "-",
      },
      {
        label: "Country of Domicile",
        id: "country_of_domicile",
        value: codeToCountryNameMap.get(data?.countryISOCode || "") || "-",
        shouldShow: !isDistributor,
      },
      {
        label: "Region",
        id: "region",
        value: data?.region || "-",
        shouldShow: !!data?.region,
      },
      {
        label: "City",
        id: "city",
        value: data?.city || "-",
        shouldShow: !!data?.city,
      },
    ],
    [data, onboardedAs, isDistributor]
  );

  const rightColumnFields = useMemo<FieldConfig[]>(
    () => [
      {
        label: "Address",
        id: "address",
        value: data?.legalEntityAddress || "-",
      },
      {
        label: "Postal Code",
        id: "postal_code",
        value: data?.postalCode || "-",
      },
      {
        label: "Company website/Online Marketplace Link",
        id: "website",
        value: data?.website || "-",
      },
      {
        label: "Industry Group",
        id: "industry_group",
        value: data?.industryGroup || "-",
      },
      {
        label: "Industry",
        id: "industry",
        value: data?.industry || "-",
      },
      {
        label: "Date of Incorporation",
        id: "date_of_incorporation",
        value: data?.companyStartDate
          ? convertDate(data.companyStartDate)
          : "-",
      },
      {
        label: "Investment Mode",
        id: "investment_mode",
        value:
          capitalizeFirstLetter(data?.investmentPreferences?.investmentMode) ||
          "-",
        shouldShow: isInvestor,
      },
      {
        label: "Planned Investment Amount (SGD)",
        id: "investment_amount",
        value: data?.investmentPreferences?.investmentSize || "-",
        shouldShow: !shouldHideInvestmentAmount,
      },
    ],
    [data, isDistributor, isInvestor, shouldHideInvestmentAmount]
  );

  const spvFields = useMemo<FieldConfig[]>(
    () =>
      spvData
        ? [
            {
              label: "Date onboarded",
              id: "spv_date_onboarded",
              value: spvData.createdAt ? convertDate(spvData.createdAt) : "-",
            },
            {
              label: "Company Name",
              id: "spv_company_name",
              value: spvData.companyName || "-",
            },
            {
              label: "Registration Number",
              id: "spv_registration_number",
              value: spvData.companyRegistrationNo || "-",
            },
            {
              label: "Legal Structure",
              id: "spv_legal_structure",
              value: spvData.legalStructure || "-",
            },
            {
              label: "Country of Incorporation",
              id: "spv_country_of_incorporation",
              value: spvData.countryOfIncorporation || "-",
            },
            {
              label: "Country of Domicile",
              id: "spv_country_of_domicile",
              value:
                codeToCountryNameMap.get(spvData.countryISOCode || "") || "-",
            },
            {
              label: "Address",
              id: "spv_address",
              value: spvData.legalEntityAddress || "-",
            },
            {
              label: "Postal Code",
              id: "spv_postal_code",
              value: spvData.postalCode || "-",
            },
            {
              label: "Company website/Online Marketplace Link",
              id: "spv_website",
              value: spvData.website || "-",
            },
            {
              label: "Industry",
              id: "spv_industry",
              value: spvData.industry || "-",
            },
          ]
        : [],
    [spvData]
  );

  // Helper function to render fields
  const renderFields = (fields: FieldConfig[]) =>
    fields
      .filter((field) => field.shouldShow !== false)
      .map((field) => (
        <InputGroupDisable
          key={field.id}
          labelText={field.label}
          id={field.id}
          value={field.value}
        />
      ));

  return (
    <>
      <div className={classes.valueContainer}>
        <div>{renderFields(leftColumnFields)}</div>
        <div>{renderFields(rightColumnFields)}</div>
      </div>

      {spvData && spvFields.length > 0 && (
        <>
          <Divider my="md" />
          <h2 style={{ marginLeft: 44, marginTop: 44, fontWeight: 700 }}>
            SPV
          </h2>
          <div className={classes.valueContainer}>
            {renderFields(spvFields)}
          </div>
        </>
      )}
    </>
  );
};

export default Organisation;
